import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductFilters, SortOption } from '../types';
import { ALL_CATEGORIES, SORT_OPTIONS, PRICE_PRESETS } from '../utils/constants';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import SkeletonGrid from '../components/SkeletonGrid';
import SEOHead from '../components/SEOHead';
import './Products.css';

const LIMIT = 28;

const Pagination: React.FC<{ page: number; pages: number; onChange: (p: number) => void }> = ({ page, pages, onChange }) => {
  if (pages <= 1) return null;

  const nums: (number | '...')[] = [1];
  const lo = Math.max(2, page - 2);
  const hi = Math.min(pages - 1, page + 2);
  if (lo > 2)         nums.push('...');
  for (let i = lo; i <= hi; i++) nums.push(i);
  if (hi < pages - 1) nums.push('...');
  if (pages > 1)      nums.push(pages);

  return (
    <div className="pagination">
      <button className="page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>← Prev</button>
      {nums.map((n, i) =>
        n === '...'
          ? <span key={`d${i}`} className="page-dots">…</span>
          : <button key={n} className={`page-btn${page === n ? ' active' : ''}`} onClick={() => onChange(n as number)}>{n}</button>
      )}
      <button className="page-btn" disabled={page === pages} onClick={() => onChange(page + 1)}>Next →</button>
    </div>
  );
};

const Products: React.FC = () => {
  const [searchParams] = useSearchParams();
  const topRef = useRef<HTMLDivElement>(null);

  const [category,     setCategory]     = useState<string>(searchParams.get('category') ?? 'All');
  const [sort,         setSort]         = useState<SortOption>('newest');
  const [inStock,      setInStock]      = useState<boolean>(false);
  const [minPrice,     setMinPrice]     = useState<string>('');
  const [maxPrice,     setMaxPrice]     = useState<string>('');
  const [searchInput,  setSearchInput]  = useState<string>(searchParams.get('search') ?? '');
  const [activeSearch, setActiveSearch] = useState<string>(searchParams.get('search') ?? '');
  const [page,         setPage]         = useState<number>(1);

  // Sync state when URL search params change (e.g. footer/navbar links)
  useEffect(() => {
    setCategory(searchParams.get('category') ?? 'All');
    setSearchInput(searchParams.get('search') ?? '');
    setActiveSearch(searchParams.get('search') ?? '');
    setPage(1);
  }, [searchParams]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [category, sort, inStock, minPrice, maxPrice, activeSearch]);

  const filters: ProductFilters = {
    ...(category !== 'All' && { category }),
    sort,
    in_stock: inStock,
    ...(minPrice      && { min_price: minPrice }),
    ...(maxPrice      && { max_price: maxPrice }),
    ...(activeSearch  && { search: activeSearch }),
    page,
    limit: LIMIT,
  };

  const { products, total, pages, loading, error, refetch } = useProducts(filters);

  const handlePageChange = (p: number): void => {
    setPage(p);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearFilters = useCallback((): void => {
    setCategory('All'); setSort('newest'); setInStock(false);
    setMinPrice('');    setMaxPrice('');
    setSearchInput(''); setActiveSearch('');
    setPage(1);
  }, []);

  return (
    <div className="products-page">
      <SEOHead
        title={category !== 'All' ? `${category} Collection` : 'Our Collections — Indian Ethnic Wear'}
        description={`Browse our ${category !== 'All' ? category : 'full'} collection of Indian ethnic wear. ${total} products available. Kurtas, suits, palazzo sets and more.`}
        keywords={`${category !== 'All' ? category + ', ' : ''}Indian ethnic wear, women kurta, palazzo set, ethnic fashion online`}
        url={category !== 'All' ? `/products?category=${encodeURIComponent(category)}` : '/products'}
        type="website"
      />
      <div className="page-header">
        <div className="container">
          <h1>Our Collections</h1>
          <p>Discover our beautiful collections</p>
        </div>
      </div>

      <div className="container products-layout">
        {/* Sidebar */}
        <aside className="filters-sidebar">
          <div className="filter-header">
            <h3>Filters</h3>
            <button onClick={clearFilters} className="clear-all-btn">Clear All</button>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <div className="filter-search">
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && setActiveSearch(searchInput)}
                     placeholder="Search products..." />
              <button onClick={() => setActiveSearch(searchInput)}>🔍</button>
            </div>
          </div>

          <div className="filter-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={inStock} onChange={e => setInStock(e.target.checked)} />
              In Stock Only
            </label>
          </div>

          <div className="filter-group">
            <label>Category</label>
            {ALL_CATEGORIES.map(cat => (
              <button key={cat}
                className={`cat-filter-btn ${category === cat ? 'active' : ''}`}
                onClick={() => setCategory(cat)}>
                {cat}
              </button>
            ))}
          </div>

          <div className="filter-group">
            <label>Price Range (₹)</label>
            <div className="price-inputs">
              <input type="number" placeholder="Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
              <span>—</span>
              <input type="number" placeholder="Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
            </div>
            <div className="price-presets">
              {PRICE_PRESETS.map(p => (
                <button key={p.label} onClick={() => { setMinPrice(p.min); setMaxPrice(p.max); }}
                        className="price-preset">{p.label}</button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Sort By</label>
            <select value={sort} onChange={e => setSort(e.target.value as SortOption)} className="sort-select">
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </aside>

        {/* Products */}
        <main className="products-main" ref={topRef}>
          <div className="products-toolbar">
            <span className="results-count">
              {loading ? 'Loading...' : (
                <>
                  {pages > 1
                    ? `${Math.min((page - 1) * LIMIT + 1, total)}–${Math.min(page * LIMIT, total)} of ${total} products`
                    : `${total} products found`}
                  {category !== 'All' && <span className="active-filter"> in {category}</span>}
                </>
              )}
            </span>
            <select value={sort} onChange={e => setSort(e.target.value as SortOption)}
                    className="sort-select mobile-sort">
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {loading ? (
            <SkeletonGrid count={12} />
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <h3>Could not load products</h3>
              <p>The server is taking too long. Please{' '}
                <button onClick={refetch} className="link-btn">try again</button>
              </p>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters or{' '}
                <button onClick={clearFilters} className="link-btn">clear all filters</button>
              </p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
              <Pagination page={page} pages={pages} onChange={handlePageChange} />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Products;
